<ng-container *transloco="let t">
<div class="modal-header">
  <h2>{{ t('auth.resetPasswordDialog.title') }}</h2>

  <!-- hack to fool ngbModal autofocus -->
  <input type="text" style="display: none" />

  @if (!spinnerVisible) {
    <div
      class="btn btn-icon btn-sm btn-active-light-primary ms-2"
      aria-label="Close"
      (click)="activeModal.dismiss('Cross click')"
    >
      <span class="svg-icon svg-icon-2x">
        <close-x-svg-icon></close-x-svg-icon>
      </span>
    </div>
  }
</div>

<div class="modal-body py-5 px-lg-17">
  <div
    class="alert alert-info bg-light-success d-flex align-items-center p-5 mb-10"
  >
    <span class="svg-icon svg-icon-2hx svg-icon-info me-3">
      <duotone-shield-check-svg-icon></duotone-shield-check-svg-icon>
    </span>
    @if (userName) {
      <div class="d-flex flex-column">
        <span class="mb-3"
          >{{ t('auth.resetPasswordDialog.codeSent') }} <strong>{{ userName }}</strong
          >.</span
        >
        <span>{{ t('auth.resetPasswordDialog.enterCodeAndPassword') }}</span>
      </div>
    }
  </div>
  @if (errorMessage || (passwordErrors && passwordErrors.length)) {
    <div
      class="alert alert-danger d-flex align-items-center p-5 mb-10"
    >
      <span class="svg-icon svg-icon-2hx svg-icon-danger me-3">
        <duotone-warning-circle-svg-icon></duotone-warning-circle-svg-icon>
      </span>
      <div class="d-flex flex-column">
        <span>{{ errorMessage }}</span>
        @for (passwordError of passwordErrors; track $index) {
          <div>
            {{ passwordError }}
          </div>
        }
      </div>
    </div>
  }
  <form
    #resetPasswordForm="ngForm"
    class="form"
    novalidate
    id="resetPasswordForm"
    name="resetPasswordForm"
  >
    <div class="fv-row mb-5">
      <label class="form-label fs-6 fw-bolder text-dark">{{ t('auth.resetPasswordDialog.securityCode') }}</label>
      <input
        class="form-control form-control-solid"
        required
        [ngClass]="{ 'is-invalid': code && code.touched && code.invalid }"
        type="text"
        name="securityCode"
        [(ngModel)]="securityCode"
        #code="ngModel"
        autocomplete="off"
      />
      <div class="invalid-feedback">{{ t('auth.resetPasswordDialog.enterSecurityCode') }}</div>
    </div>
    @if (userName) {
      <div class="fv-row mb-5">
        <label class="form-label fs-6 fw-bolder text-dark">{{ t('auth.resetPassword.company') }}</label>
        <select
          class="form-control form-control-solid"
          required
          [ngClass]="{
            'is-invalid': company && company.touched && company.invalid
          }"
          name="company"
          [(ngModel)]="userCompanyGuid"
          (ngModelChange)="onCompanyChanged()"
          #company="ngModel"
          autocomplete="off"
        >
          <option value="">{{ t('common.selectOne') }}</option>
          @for (company of availableCompanies; track company.userCompanyGuid) {
            <option
              [ngValue]="company.userCompanyGuid"
            >
              {{ company.companyName }}
            </option>
          }
        </select>
        <div class="invalid-feedback">{{ t('auth.resetPassword.selectCompany') }}</div>
      </div>
    }
    <div class="fv-row mb-5">
      <label class="form-label fw-bolder text-dark fs-6">{{ t('auth.resetPassword.password') }}</label>
      <div class="position-relative">
        <input
          #passwordInput
          type="password"
          class="form-control form-control-solid has-visibility-toggle"
          [(ngModel)]="password"
          autocomplete="off"
          placeholder=""
          name="password3"
          id="password3"
          required
          [minlength]="passwordPolicy?.minimumPasswordLength || 8"
          (focus)="op3.show($event, passwordInput)"
          (mousedown)="op3.show($event, passwordInput)"
          (ngModelChange)="onPasswordChanged()"
          [ngClass]="{
            'is-invalid':
              userPassword && userPassword.touched && userPassword.invalid
          }"
          #userPassword="ngModel"
          validateEqual="confirmedPassword3"
          reverse="true"
        />
        <p-popover
          #op3
          appendTo="body"
          styleClass="password-validation-overlay"
        >
          <ng-template pTemplate="content">
            <div>
              <div
                class="d-flex password-validation-indicator"
                [ngClass]="{
                  'text-success': passwordIsMinimum8Characters
                }"
              >
                <i
                  class="far password-validation-indicator-icon fa-sad-tear"
                  [ngClass]="{
                    'fa-smile': passwordIsMinimum8Characters,
                    'text-success': passwordIsMinimum8Characters
                  }"
                ></i>
                <div>{{ t('auth.resetPassword.minChars') }}</div>
              </div>
              @if (!passwordPolicy || passwordPolicy.requireDigit) {
              <div
                class="d-flex password-validation-indicator"
                [ngClass]="{
                  'text-success': passwordContainsDigit
                }"
              >
                <i
                  class="far password-validation-indicator-icon fa-sad-tear"
                  [ngClass]="{
                    'fa-smile': passwordContainsDigit,
                    'text-success': passwordContainsDigit
                  }"
                ></i>
                <div>{{ t('auth.resetPassword.includeNumber') }}</div>
              </div>
              }
              @if (!passwordPolicy || passwordPolicy.requireLowercase) {
              <div
                class="d-flex password-validation-indicator"
                [ngClass]="{
                  'text-success': passwordContainsLowercase
                }"
              >
                <i
                  class="far password-validation-indicator-icon fa-sad-tear"
                  [ngClass]="{
                    'fa-smile': passwordContainsLowercase,
                    'text-success': passwordContainsLowercase
                  }"
                ></i>
                <div>{{ t('auth.resetPassword.includeLowercase') }}</div>
              </div>
              }
              @if (!passwordPolicy || passwordPolicy.requireUppercase) {
              <div
                class="d-flex password-validation-indicator"
                [ngClass]="{
                  'text-success': passwordContainsUppercase
                }"
              >
                <i
                  class="far password-validation-indicator-icon fa-sad-tear"
                  [ngClass]="{
                    'fa-smile': passwordContainsUppercase,
                    'text-success': passwordContainsUppercase
                  }"
                ></i>
                <div>{{ t('auth.resetPassword.includeUppercase') }}</div>
              </div>
              }
              @if (!passwordPolicy || passwordPolicy.requireSpecialCharacter) {
              <div
                class="d-flex password-validation-indicator"
                [ngClass]="{
                  'text-success': passwordContainsSpecialCharacter
                }"
              >
                <i
                  class="far password-validation-indicator-icon fa-sad-tear"
                  [ngClass]="{
                    'fa-smile': passwordContainsSpecialCharacter,
                    'text-success': passwordContainsSpecialCharacter
                  }"
                ></i>
                <div>{{ t('auth.resetPassword.includeSpecial') }}</div>
              </div>
              }
            </div>
          </ng-template>
        </p-popover>

        <span
          class="btn btn-sm btn-icon position-absolute translate-middle top-50 end-0 me-n2"
          data-kt-password-meter-control="visibility"
          (click)="op3.toggle($event, passwordInput)"
        >
          @if (!passwordInputState.displayed) {
            <i
              class="bi bi-eye-slash fs-2"
              (click)="onPasswordStateClicked(passwordInputState)"
              (touchend)="onPasswordStateClicked(passwordInputState)"
            ></i>
          }
          @if (passwordInputState.displayed) {
            <i
              class="bi bi-eye fs-2"
              (click)="onPasswordStateClicked(passwordInputState)"
              (touchend)="onPasswordStateClicked(passwordInputState)"
            ></i>
          }
        </span>
      </div>
      @if (
        userPassword.touched &&
        userPassword.errors &&
        userPassword.errors.required
      ) {
        <div
          class="d-block invalid-feedback"
        >
          {{ t('auth.resetPassword.enterPassword') }}
        </div>
      }
      @if (
        userPassword.touched &&
        userPassword.errors &&
        !userPassword.errors.required
      ) {
        <div
          class="d-block invalid-feedback"
        >
          {{ t('auth.resetPassword.passwordRequirements') }}
        </div>
      }
      @if (
        userPassword.errors &&
        userPassword.errors.validateEqual
      ) {
        <div
          class="d-block invalid-feedback"
        >
          {{ t('auth.resetPassword.passwordMismatch') }}
        </div>
      }
    </div>
    <div class="fv-row mb-5">
      <label class="form-label fw-bolder text-dark fs-6"
        >{{ t('auth.resetPassword.confirmPassword') }}</label
      >
      <div class="position-relative">
        <input
          type="password"
          class="form-control form-control-solid has-visibility-toggle"
          [(ngModel)]="confirmedPassword"
          autocomplete="off"
          required
          placeholder=""
          name="confirmedPassword3"
          id="confirmedPassword3"
          [ngClass]="{
            'is-invalid':
              userConfirmedPassword &&
              userConfirmedPassword.touched &&
              userConfirmedPassword.invalid
          }"
          #userConfirmedPassword="ngModel"
          validateEqual="password3"
        />
        <span
          class="btn btn-sm btn-icon position-absolute translate-middle top-50 end-0 me-n2"
          data-kt-password-meter-control="visibility"
        >
          @if (!confirmPasswordInputState.displayed) {
            <i
              class="bi bi-eye-slash fs-2"
              (click)="onPasswordStateClicked(confirmPasswordInputState)"
              (touchend)="onPasswordStateClicked(confirmPasswordInputState)"
            ></i>
          }
          @if (confirmPasswordInputState.displayed) {
            <i
              class="bi bi-eye fs-2"
              (click)="onPasswordStateClicked(confirmPasswordInputState)"
              (touchend)="onPasswordStateClicked(confirmPasswordInputState)"
            ></i>
          }
        </span>
      </div>
      @if (
        userConfirmedPassword.touched &&
        userConfirmedPassword.errors &&
        userConfirmedPassword.errors.required
      ) {
        <div
          class="d-block invalid-feedback"
        >
          {{ t('auth.resetPassword.confirmPasswordPlaceholder') }}
        </div>
      }
      @if (
        userConfirmedPassword.errors &&
        userConfirmedPassword.errors.validateEqual
      ) {
        <div
          class="d-block invalid-feedback"
        >
          {{ t('auth.resetPassword.passwordMismatch') }}
        </div>
      }
    </div>
  </form>
</div>

<div class="modal-footer flex-center">
  @if (!spinnerVisible) {
    <button
      type="button"
      class="btn btn-white me-3"
      (click)="activeModal.dismiss()"
    >
      {{ t('common.cancel') }}
    </button>
  }
  <button
    type="submit"
    class="btn btn-primary"
    (click)="onResendCodeClicked()"
    [disabled]="spinnerVisible"
  >
    <span class="indicator-label">{{ 'auth.resetPasswordDialog.resendCode' | transloco }}</span>
    @if (spinnerVisible) {
      <span
        class="spinner-border spinner-border-sm align-middle ms-2"
      ></span>
    }
  </button>
  <button
    type="submit"
    class="btn btn-primary"
    (click)="onResetPasswordClicked()"
    [disabled]="spinnerVisible"
  >
    <span class="indicator-label">{{ spinnerVisible ? ('common.pleaseWait' | transloco) : ('auth.resetPasswordDialog.title' | transloco) }}</span>
    @if (spinnerVisible) {
      <span
        class="spinner-border spinner-border-sm align-middle ms-2"
      ></span>
    }
  </button>
</div>
</ng-container>
